import { CircleDollarSign, Menu as MenuIcon, Settings2, Trash2 } from '@tamagui/lucide-icons';
import { useQueryClient } from '@tanstack/react-query';
import { Confirm } from '@unistocks/components/confirm/confirm';
import { Spinner } from '@unistocks/components/loading/spinner/spinner';
import { Menu } from '@unistocks/components/menu/menu';
import { HeaderIcon } from '@unistocks/components/modal/header-icon';
import { HeaderButton } from '@unistocks/components/page/header/header-button/header-button';
import { FullScreenSheet } from '@unistocks/components/sheet/full-screen-sheet';
import { toast } from '@unistocks/components/toast';
import { EditSavingsGoalForm } from '@unistocks/features/savings-goals/components/forms/edit-savings-goal-form';
import { SavingsGoalActions } from '@unistocks/features/savings-goals/store/savings-goals.slice';
import { invalidateSavingsGoalQueryById } from '@unistocks/features/savings-goals/utils';
import { SavingsGoalCreateOrUpdateDto, SavingsGoalDto } from '@unistocks/proxy/axios-client';
import {
  savingsGoalsGetListQueryKey,
  useSavingsGoalsDeleteMutation,
  useSavingsGoalsUpdateMutation
} from '@unistocks/proxy/axios-client/SavingsGoalsQuery';
import { useAppDispatch } from '@unistocks/shared/hooks/redux-hooks';
import { t } from '@unistocks/shared/localization/index.ts';
import { useRouter } from 'expo-router';
import { memo, useMemo, useState } from 'react';

export type SavingsGoalMenuProps = {
  savingsGoal: SavingsGoalDto;
};

export const SavingsGoalMenu = memo<SavingsGoalMenuProps>(({ savingsGoal }) => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const initialValues: SavingsGoalCreateOrUpdateDto = useMemo(
    () => ({
      ...savingsGoal,
      currency: savingsGoal.currency.code
    }),
    [savingsGoal]
  );

  const { mutateAsync: updateSavingsGoal } = useSavingsGoalsUpdateMutation(savingsGoal.id, {
    onSuccess: () => {
      invalidateSavingsGoalQueryById(queryClient, savingsGoal.id);
      setSettingsOpen(false);
      toast.success(t('::SavingsGoalUpdatedSuccessfully', { 0: savingsGoal.name }));
    }
  });

  const { mutate: deleteSavingsGoal, isPending } = useSavingsGoalsDeleteMutation(savingsGoal.id, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: savingsGoalsGetListQueryKey()
      });
      dispatch(SavingsGoalActions.setSelectedSavingsGoalId(undefined));
      toast.success(t('::SavingsGoalDeletedSuccessfully', { 0: savingsGoal.name }));
      router.replace({ pathname: '/' });
    }
  });

  return (
    <>
      <Menu
        trigger={<HeaderButton Icon={MenuIcon} />}
        headerTitle={savingsGoal.name}
        headerIcon={<HeaderIcon Icon={CircleDollarSign} />}
        contentContainerProps={{ minW: '$10' }}
      >
        <Menu.Item
          disabled={isPending}
          icon={<Settings2 color={'$unistocks'} />}
          title={t('::EditWhat', { 0: savingsGoal.name })}
          onPress={() => setSettingsOpen(true)}
        />
        <Confirm message={t('::SavingsGoal:Delete:Confirm')} onConfirm={deleteSavingsGoal}>
          <Menu.Item
            closeOnPress={false}
            disabled={isPending}
            icon={<Trash2 color={'$danger'} />}
            iconAfter={isPending ? <Spinner /> : undefined}
            title={t('::DeleteWhat', {
              0: t('::SavingsGoal').toLocaleLowerCase()
            })}
          />
        </Confirm>
      </Menu>

      <FullScreenSheet
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        contentTopMargin
        headerTitle={t('::EditWhat', { 0: savingsGoal.name })}
        headerIcon={<HeaderIcon Icon={Settings2} />}
      >
        <EditSavingsGoalForm
          onSubmit={updateSavingsGoal}
          onCancel={() => setSettingsOpen(false)}
          initialValues={initialValues}
        />
      </FullScreenSheet>
    </>
  );
});

SavingsGoalMenu.displayName = 'SavingsGoalMenu';
